import { Link } from "react-router-dom";
import {
  Globe,
  Smartphone,
  Cpu,
  Copyright,
  BadgeCheck,
  BarChart3,
  Megaphone,
  Check,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "../lib/constants";
import { Reveal } from "../components/Motion";
import CTASection from "../components/CTASection";

const ICONS = {
  "website-development": Globe,
  "app-development": Smartphone,
  "ai-automation": Cpu,
  "copyright-strategy": Copyright,
  "trademark-strategy": BadgeCheck,
  "benchmark-intelligence": BarChart3,
  "marketing-growth": Megaphone,
};

export default function Services() {
  return (
    <main data-testid="services-page" className="pt-32">
      {/* HERO */}
      <section className="px-6 md:px-8 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="overline">Services</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium leading-[0.95] tracking-tight mt-6 max-w-5xl">
              Seven disciplines, engineered for{" "}
              <em className="text-[#D4AF37] not-italic">scale</em>.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">
              From the first line of code to a global trademark portfolio, our
              practices are designed to compound. Pick one, or build a complete
              growth engine.
            </p>
          </Reveal>

          {/* Quick nav */}
          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-wrap gap-2">
              {SERVICES.map((s, i) => (
                <a
                  key={s.slug}
                  href={`#${s.slug}`}
                  data-testid={`services-nav-${s.slug}`}
                  className="border border-white/10 px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition-all"
                >
                  0{i + 1} · {s.name}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICE DETAILS */}
      <section className="px-6 md:px-8">
        <div className="max-w-7xl mx-auto">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.slug] || Globe;
            return (
              <div
                key={s.slug}
                id={s.slug}
                data-testid={`service-detail-${s.slug}`}
                className="border-t border-white/10 py-20 md:py-28"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  {/* LEFT — sticky header */}
                  <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-32">
                      <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center">
                          <Icon size={20} strokeWidth={1.5} className="text-[#D4AF37]" />
                        </div>
                        <span className="font-mono text-xs text-white/40">
                          / 0{i + 1}
                        </span>
                      </div>
                      <h2 className="font-display text-4xl md:text-5xl font-medium leading-[1.02] tracking-tight">
                        {s.name}
                      </h2>
                      <p className="mt-6 text-white/60 leading-relaxed text-base">
                        {s.overview}
                      </p>

                      <Link
                        to="/contact"
                        data-testid={`service-pricing-${s.slug}`}
                        className="btn-gold text-xs mt-10"
                      >
                        Request Pricing
                        <ArrowUpRight size={14} />
                      </Link>
                    </div>
                  </div>

                  {/* RIGHT — benefits & deliverables */}
                  <div className="lg:col-span-7 space-y-12">
                    <div>
                      <h3 className="overline mb-6">Benefits</h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {s.benefits.map((b) => (
                          <li
                            key={b}
                            className="flex items-start gap-3 text-white/75 text-sm leading-relaxed border border-white/5 p-5 bg-[#0E0E0E]"
                          >
                            <Check
                              size={16}
                              className="text-[#D4AF37] mt-0.5 shrink-0"
                              strokeWidth={2}
                            />
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="overline mb-6">Deliverables</h3>
                      <div className="border border-white/5 divide-y divide-white/5">
                        {s.deliverables.map((d, idx) => (
                          <div
                            key={d}
                            className="flex items-center justify-between p-5 bg-[#0E0E0E] hover:bg-[#141414] transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <span className="font-mono text-xs text-white/30">
                                {String(idx + 1).padStart(2, "0")}
                              </span>
                              <span className="text-sm text-white/80">{d}</span>
                            </div>
                            <ArrowUpRight
                              size={14}
                              className="text-white/30"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CTASection testId="services-cta" />
    </main>
  );
}
